import { useEffect, useRef, useState } from "react";
import { Instagram, Mail, MessageCircle, Play, X } from "lucide-react";
import { siteContent } from "../content/loadContent";
import type { MediaItem } from "../content/types";
import { PageCopy, PageIndex, PageMedia } from "../components/site/PagePrimitives";
import "./pages.css";

export function MediaPage() {
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const openItem = (item: MediaItem, target: HTMLElement) => {
    returnFocusRef.current = target;
    setSelected(item);
  };

  useEffect(() => {
    if (!selected) return;
    closeRef.current?.focus();
    document.body.classList.add("media-open");
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
      if (event.key === "Tab") {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("media-open");
      window.removeEventListener("keydown", handleKeyDown);
      returnFocusRef.current?.focus();
    };
  }, [selected]);

  return (
    <article className="experience-page page-media-route" aria-labelledby="media-title">
      <PageMedia src="/assets/athlete/media-tunnel.webp" alt="" className="page-media--media" />
      <PageIndex number="05" label="MEDIA" />
      <PageCopy className="media-copy">
        <p className="page-kicker">REPLAY THE MOMENT</p>
        <h1 id="media-title">KEEP<br />GOING.</h1>
        <p>每一刻，都值得记录。</p>
      </PageCopy>

      <div className="media-rail" aria-label="训练影像">
        <div className="media-rail__stage">
          <div className="media-rail__track">
            {[0, 1].map((groupIndex) => (
              <div
                key={groupIndex}
                className="media-rail__group"
                aria-hidden={groupIndex === 1 ? "true" : undefined}
              >
                {siteContent.stream.map((item, itemIndex) =>
                  groupIndex === 0 ? (
                    <button
                      key={`${item.src}-${itemIndex}`}
                      type="button"
                      className="media-frame"
                      onClick={(event) => {
                        openItem(item, event.currentTarget);
                      }}
                      onPointerDown={(event) => {
                        if (event.button !== 0) return;
                        openItem(item, event.currentTarget);
                      }}
                      aria-label={`打开影像：${item.caption}`}
                    >
                      {renderFrameContent(item)}
                    </button>
                  ) : (
                    <div key={`${item.src}-${itemIndex}`} className="media-frame media-frame--duplicate">
                      {renderFrameContent(item)}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="media-contact">
        <a href={`mailto:${siteContent.socials.email}`}>
          <Mail size={18} aria-hidden="true" />
          <span>{siteContent.socials.email}</span>
        </a>
        <span><Instagram size={18} aria-hidden="true" />{siteContent.socials.instagram}</span>
        <span><MessageCircle size={18} aria-hidden="true" />{siteContent.socials.wechat}</span>
      </footer>

      {selected ? (
        <div
          className="media-modal"
          role="dialog"
          aria-modal="true"
          aria-label={selected.caption}
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setSelected(null);
          }}
        >
          <figure>
            <button
              ref={closeRef}
              type="button"
              onClick={() => setSelected(null)}
              aria-label="关闭影像"
              title="关闭影像"
            >
              <X size={24} aria-hidden="true" />
            </button>
            {selected.type === "video" ? (
              <video src={selected.src} poster={selected.poster ?? undefined} controls autoPlay playsInline>
                你的浏览器暂不支持视频播放。
              </video>
            ) : (
              <img src={selected.src} alt={selected.caption} />
            )}
            <figcaption>{selected.caption}</figcaption>
          </figure>
        </div>
      ) : null}
    </article>
  );
}

function renderFrameContent(item: MediaItem) {
  return (
    <>
      <img src={item.poster ?? item.src} alt="" loading="lazy" width={520} height={300} />
      <span className="media-frame__play">
        <Play size={18} fill="currentColor" aria-hidden="true" />
      </span>
      <strong>{item.caption}</strong>
    </>
  );
}
